import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { baseApiURL } from "../../baseUrl";
import {
  FiSend,
  FiCpu,
  FiMessageSquare,
  FiInfo,
  FiPaperclip,
  FiX,
  FiFileText,
  FiDatabase,
  FiDownload,
  FiGlobe,
  FiExternalLink,
  FiSearch,
  FiLayers,
  FiPlusCircle,
  FiCheckCircle,
  FiZap
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

const AIAssistant = () => {
  const router = useLocation();
  const [activeTab, setActiveTab] = useState("chat"); // 'chat' | 'rag' | 'web'
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAiMode, setIsAiMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // RAG & Materials State
  const [ragQuery, setRagQuery] = useState("");
  const [ragResults, setRagResults] = useState(null);
  const [loadingRag, setLoadingRag] = useState(false);

  // Open Source Web Scraper State
  const [webQuery, setWebQuery] = useState("Data Structures");
  const [webBooks, setWebBooks] = useState([]);
  const [loadingWeb, setLoadingWeb] = useState(false);
  const [vectorizingId, setVectorizingId] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const userRole = router.state?.type?.toLowerCase() || "student";

  const suggestions = [
    { label: "📊 Check my attendance", text: "What is my current attendance percentage?" },
    { label: "📚 Search Study Materials", text: "What study materials and PDFs are available for my branch?" },
    { label: "📅 Show my timetable", text: "Show my class schedule / timetable for the semester." },
    { label: "📝 Leave application format", text: "Give me a leave application format." },
    { label: "🏆 Predict academic risk", text: "Am I currently at any academic or attendance risk?" }
  ];

  // Welcome message
  useEffect(() => {
    const welcomeMsg = {
      id: "welcome",
      sender: "bot",
      text: `👋 **Welcome to the ECAP AI & RAG Academic Assistant!**

I can answer general questions (coding, math, writing, etc.) and analyze uploaded images or documents (PDF, Text).

I also have direct access to:
- 📊 **Attendance, Marks & Timetables**
- 📚 **Uploaded Faculty Study Materials & Question Papers (with direct PDF links)**
- 📍 **Library Catalog Books & Rack Locations**
- 🌐 **Open Source Web Scraped Textbooks**

How can I help you today? You can type a question, upload a file, or select one of the quick options below!`,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };
    setMessages([welcomeMsg]);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (activeTab === "chat") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, activeTab]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedFile({
        name: file.name,
        mimeType: file.type,
        base64: reader.result.split(",")[1]
      });
      toast.success(`Attached: ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSendMessage = async (textToSend) => {
    const query = (textToSend || inputText).trim();
    if (!query && !selectedFile) return;

    const userMessage = {
      id: Date.now().toString(),
      sender: "user",
      text: query,
      attachment: selectedFile,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    const fileToSend = selectedFile;
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${baseApiURL()}/ai/assistant/chat`,
        {
          message: query,
          role: userRole,
          attachment: fileToSend
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setIsAiMode(res.data.isAiMode);
        const botMessage = {
          id: (Date.now() + 1).toString(),
          sender: "bot",
          text: res.data.response,
          sources: res.data.sources || [],
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        toast.error("AI Assistant could not process your request.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error communicating with AI Assistant");
    } finally {
      setLoading(false);
    }
  };

  const handleRAGSearch = async () => {
    if (!ragQuery.trim()) return;
    setLoadingRag(true);
    try {
      const res = await axios.post(`${baseApiURL()}/library/rag/chat`, {
        query: ragQuery,
        branch: "All",
        documentType: "all"
      });
      if (res.data.success) {
        setRagResults(res.data);
      }
    } catch (err) {
      toast.error("RAG search failed");
    } finally {
      setLoadingRag(false);
    }
  };

  const handleWebSearch = async (queryToSearch) => {
    const q = queryToSearch || webQuery;
    if (!q.trim()) return;
    setLoadingWeb(true);
    try {
      const res = await axios.get(`${baseApiURL()}/library/rag/web-scrape-books`, {
        params: { q, limit: 12 }
      });
      if (res.data.success) {
        setWebBooks(res.data.books || []);
        toast.success(`Found ${res.data.books.length} open textbooks!`);
      }
    } catch (err) {
      toast.error("Web scraping query failed");
    } finally {
      setLoadingWeb(false);
    }
  };

  const handleVectorizeBook = async (book) => {
    setVectorizingId(book.id);
    const toastId = toast.loading("Vectorizing into RAG index...");
    try {
      const res = await axios.post(`${baseApiURL()}/library/rag/ingest`, {
        title: book.title,
        documentType: "catalog",
        content: `${book.title} Author: ${book.author} Published: ${book.publishedYear}. Summary: ${book.summary}`,
        summary: book.summary,
        author: book.author,
        publishedYear: book.publishedYear !== "N/A" ? Number(book.publishedYear) : undefined,
        branch: "General",
        fileUrl: book.readUrl,
        tags: book.subjects || []
      });
      if (res.data?.success) {
        toast.success(`"${book.title}" added to AI RAG Index!`, { id: toastId });
      }
    } catch (err) {
      toast.error("Failed to vectorize book", { id: toastId });
    } finally {
      setVectorizingId(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* 🌟 Header Banner */}
      <div className="bento-header-banner flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-3.5">
          <div className="relative">
            <div className="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 flex items-center justify-center shadow-xs">
              <FiCpu className="w-5 h-5 animate-pulse" />
            </div>
            <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white animate-ping" />
            <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                ECAP Campus AI & RAG Assistant
              </h1>
              <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-100 dark:border-indigo-500/20 flex items-center gap-1">
                <FiZap className="w-3 h-3 text-amber-500" /> {isAiMode ? "LLM Powered Engine" : "Active Standard Engine"}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
              Ask about timetables, attendance, study materials, PYQs, and open-source web books
            </p>
          </div>
        </div>

        <div className="hidden sm:flex items-center text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-800 font-medium">
          <FiInfo className="mr-1.5 text-indigo-600 dark:text-indigo-400 w-4 h-4" />
          <span>Supports image/PDF upload & RAG vector search</span>
        </div>
      </div>

      {/* 🔲 Navigation Tabs */}
      <div className="bento-card p-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl flex items-center gap-1.5 overflow-x-auto">
        <button
          onClick={() => setActiveTab("chat")}
          className={`flex items-center space-x-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer ${
            activeTab === "chat"
              ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200 dark:border-slate-700"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-800/50"
          }`}
        >
          <FiMessageSquare className="w-4 h-4" />
          <span>AI Academic Assistant</span>
        </button>

        <button
          onClick={() => setActiveTab("rag")}
          className={`flex items-center space-x-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer ${
            activeTab === "rag"
              ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200 dark:border-slate-700"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-800/50"
          }`}
        >
          <FiDatabase className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          <span>RAG Database & Materials</span>
        </button>

        <button
          onClick={() => {
            setActiveTab("web");
            if (webBooks.length === 0) handleWebSearch("Data Structures");
          }}
          className={`flex items-center space-x-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer ${
            activeTab === "web"
              ? "bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 shadow-xs border border-slate-200 dark:border-slate-700"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-800/50"
          }`}
        >
          <FiGlobe className="w-4 h-4 text-emerald-600 dark:text-emerald-400 animate-pulse" />
          <span>Open Source Web Scraper</span>
        </button>
      </div>

      {/* Main Glassmorphic Light / Dark Content Box */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 rounded-2xl overflow-hidden shadow-xs dark:shadow-xl min-h-[600px] flex flex-col">

        {/* VIEW 1: AI ACADEMIC CHAT */}
        {activeTab === "chat" && (
          <div className="flex-1 flex flex-col h-[600px]">
            {/* Messages area */}
            <div className="flex-1 p-4 md:p-6 overflow-y-auto space-y-4">
              <AnimatePresence initial={false}>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[78%] rounded-2xl px-4 py-3 shadow-xs ${
                        msg.sender === "user"
                          ? "bg-indigo-600 text-white rounded-br-none"
                          : "bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 rounded-bl-none border border-slate-200 dark:border-slate-800"
                      }`}
                    >
                      {/* Attachment Render */}
                      {msg.attachment && (
                        <div className="mb-2.5 pb-2 border-b border-slate-200 dark:border-slate-800 max-w-full">
                          {msg.attachment.mimeType.startsWith("image/") ? (
                            <img
                              src={`data:${msg.attachment.mimeType};base64,${msg.attachment.base64}`}
                              alt="Attachment"
                              className="max-h-48 rounded-lg object-contain bg-slate-100 dark:bg-slate-950/40 p-1 border border-slate-200 dark:border-white/10"
                            />
                          ) : (
                            <div className="flex items-center gap-2.5 p-2.5 bg-slate-100 dark:bg-slate-950/40 rounded-xl border border-slate-200 dark:border-white/5">
                              <FiFileText className="text-indigo-600 dark:text-cyan-400 w-5 h-5 flex-shrink-0" />
                              <div className="overflow-hidden">
                                <p className="text-xs font-semibold truncate text-slate-800 dark:text-slate-200">{msg.attachment.name}</p>
                                <p className="text-[9px] text-slate-500 dark:text-slate-400 capitalize">{msg.attachment.mimeType.split("/")[1]} Document</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Text Render with PDF Download Card Interceptor */}
                      <div className="text-sm leading-relaxed whitespace-pre-wrap space-y-1">
                        {msg.text.split("\n").map((line, lIdx) => {
                          if (!line || !line.trim()) return null;

                          // Detect Cloudinary / S3 / HTTP PDF Links and render interactive PDF cards!
                          const urlMatch = line.match(/(https?:\/\/[^\s\)\"]+)/i);
                          if (urlMatch) {
                            const rawUrl = urlMatch[1];
                            const cleanUrl = rawUrl.replace(/[\)\>\]]+$/, "");

                            let fileTitle = "";

                            // 1. Check for bold title **Title**
                            const boldMatch = line.match(/\*\*([^*]+)\*\*/);
                            if (boldMatch && boldMatch[1]) {
                              fileTitle = boldMatch[1].replace(/\[|\]/g, "").trim();
                            }

                            // 2. Check for quoted title "Title"
                            if (!fileTitle) {
                              const quoteMatch = line.match(/["“']([^"”']+)["”']/);
                              if (quoteMatch && quoteMatch[1]) {
                                fileTitle = quoteMatch[1].trim();
                              }
                            }

                            // 3. Check for markdown link label [Title](url)
                            if (!fileTitle) {
                              const linkMatch = line.match(/\[([^\]]+)\]\([^\)]+\)/);
                              if (linkMatch && linkMatch[1] && !linkMatch[1].startsWith("http")) {
                                fileTitle = linkMatch[1].trim();
                              }
                            }

                            // 4. Fallback: Clean line text
                            if (!fileTitle) {
                              fileTitle = line.replace(/\[\s*(https?:\/\/[^\s\]]+)\s*\]\([^\)]+\)/gi, "")
                                                  .replace(/(https?:\/\/[^\s\)\"]+)/gi, "")
                                                  .replace(/\*+|\-+|\:|Link|Faculty|Sem|Semester/gi, "")
                                                  .trim();
                            }

                            // 5. Final fallback to URL filename
                            if (!fileTitle || fileTitle.length < 2) {
                              const parts = cleanUrl.split("/");
                              fileTitle = decodeURIComponent(parts[parts.length - 1] || "Study Material PDF").replace(/_\d+$/, "");
                            }

                            return (
                              <div key={lIdx} className="my-2 p-3 bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-indigo-500/50 transition flex items-center justify-between gap-3 shadow-xs">
                                <div className="flex items-center gap-2.5 overflow-hidden">
                                  <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/80 border border-red-200 dark:border-red-800/60 flex items-center justify-center text-red-600 dark:text-red-400 flex-shrink-0">
                                    <FiFileText className="w-4 h-4" />
                                  </div>
                                  <div className="overflow-hidden">
                                    <p className="text-xs font-bold text-slate-900 dark:text-slate-200 truncate">
                                      {fileTitle || "Study Material PDF"}
                                    </p>
                                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-800/50 inline-block mt-0.5">
                                      PDF Document
                                    </span>
                                  </div>
                                </div>

                                <a
                                  href={cleanUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex-shrink-0 inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95"
                                >
                                  <FiDownload className="w-3.5 h-3.5" />
                                  <span>View / Download PDF</span>
                                </a>
                              </div>
                            );
                          }

                          if (line.startsWith("### ") || line.startsWith("## ")) {
                            return <h3 key={lIdx} className="font-bold text-indigo-600 dark:text-cyan-300 mt-2 mb-1 text-sm">{line.replace(/#+\s/, "")}</h3>;
                          }
                          if (line.startsWith("- ") || line.startsWith("* ")) {
                            return <li key={lIdx} className="ml-3 list-disc my-0.5 text-slate-700 dark:text-slate-200">{line.substring(2)}</li>;
                          }
                          return <p key={lIdx} className="my-0.5">{line}</p>;
                        })}
                      </div>

                      <div className="text-[9px] text-right mt-1.5 opacity-60">{msg.time}</div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {loading && (
                <div className="flex justify-start items-center space-x-2">
                  <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3 rounded-bl-none flex items-center space-x-2">
                    <span className="w-2.5 h-2.5 bg-indigo-600 dark:bg-cyan-400 rounded-full animate-bounce" />
                    <span className="w-2.5 h-2.5 bg-blue-500 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2.5 h-2.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    <span className="text-xs text-slate-500 dark:text-slate-400">ECAP AI Processing...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {messages.length <= 2 && !selectedFile && (
              <div className="px-4 md:px-6 py-3 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 overflow-x-auto whitespace-nowrap flex gap-2">
                {suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSendMessage(s.text)}
                    className="inline-flex items-center text-xs bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950 text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-300 px-3.5 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all shadow-xs font-semibold cursor-pointer"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Selected File Drawer */}
            {selectedFile && (
              <div className="px-4 py-2 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 overflow-hidden">
                  {selectedFile.mimeType.startsWith("image/") ? (
                    <img
                      src={`data:${selectedFile.mimeType};base64,${selectedFile.base64}`}
                      alt="Upload preview"
                      className="w-10 h-10 rounded object-cover bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-850 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                      <FiFileText className="text-indigo-600 dark:text-cyan-400 w-5 h-5" />
                    </div>
                  )}
                  <div className="overflow-hidden">
                    <p className="text-xs text-slate-900 dark:text-slate-200 truncate font-semibold">{selectedFile.name}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase">{selectedFile.mimeType.split("/")[1]}</p>
                  </div>
                </div>
                <button
                  onClick={removeSelectedFile}
                  className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-all shadow-xs cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Input panel */}
            <div className="p-4 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-900 flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,application/pdf,text/plain"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                title="Upload image or document"
                className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center justify-center transition-all disabled:opacity-40 cursor-pointer"
              >
                <FiPaperclip className="w-5 h-5" />
              </button>

              <textarea
                rows={1}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask me anything, request study materials or upload notes..."
                className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 text-sm rounded-xl px-4 py-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none max-h-24"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={loading || (!inputText.trim() && !selectedFile)}
                className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center text-white hover:bg-indigo-500 transition-all disabled:opacity-40 shadow-md shadow-indigo-600/20 cursor-pointer"
              >
                <FiSend className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* VIEW 2: RAG DATABASE MATERIALS MODE */}
        {activeTab === "rag" && (
          <div className="flex-1 p-6 overflow-y-auto space-y-6">
            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
              <h3 className="text-sm font-bold text-indigo-600 dark:text-cyan-400 flex items-center gap-2">
                <FiDatabase className="w-5 h-5" /> Direct Vector RAG Search across Study Materials & Question Papers
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Type any subject or topic to search faculty-uploaded PDFs, previous year question papers (PYQs), and library books.
              </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={ragQuery}
                  onChange={(e) => setRagQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleRAGSearch()}
                  placeholder="Enter subject or topic (e.g. HCI, Data Structures, DBMS, Machine Learning)..."
                  className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                />
                <button
                  onClick={() => handleRAGSearch()}
                  disabled={loadingRag}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs px-6 py-2.5 rounded-xl transition flex items-center gap-2 disabled:opacity-50 cursor-pointer shadow-xs"
                >
                  <FiSearch className="w-4 h-4" /> Search Vectors
                </button>
              </div>
            </div>

            {loadingRag ? (
              <div className="flex flex-col items-center justify-center h-48 space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-cyan-400" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Querying Pinecone Vector Index & MongoDB Materials...</span>
              </div>
            ) : ragResults ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-xs text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                  <span className="font-bold">Retrieved {ragResults.sources?.length || 0} Material Results</span>
                  <span className="text-indigo-600 dark:text-cyan-400 font-mono text-[10px]">Pinecone Vector Engine</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ragResults.sources?.map((item, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-indigo-500/50 p-4 rounded-2xl transition space-y-2.5 flex flex-col justify-between shadow-xs">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 rounded-full border border-indigo-200 dark:border-indigo-800">
                            {item.documentType?.replace("_", " ")}
                          </span>
                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                            {item.score}% Match
                          </span>
                        </div>

                        <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-snug">
                          {item.title}
                        </h4>

                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">
                          {item.summary}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                        <span className="text-slate-500 dark:text-slate-400 text-[11px]">{item.rackNumber ? `📍 Rack ${item.rackNumber}` : item.branch}</span>
                        {item.fileUrl ? (
                          <a
                            href={item.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-indigo-600 text-white font-bold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-xs transition hover:bg-indigo-500"
                          >
                            <FiDownload className="w-3.5 h-3.5" /> View / Download PDF
                          </a>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400 font-semibold">{item.bookCode || "Library Ref"}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-16 text-slate-500 space-y-2 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-200 dark:border-slate-800">
                <FiDatabase className="w-12 h-12 mx-auto text-slate-400 dark:text-slate-700" />
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-400">Search for study notes, lecture PDFs, and question papers directly.</p>
              </div>
            )}
          </div>
        )}

        {/* VIEW 3: OPEN SOURCE WEB SCRAPER MODE */}
        {activeTab === "web" && (
          <div className="flex-1 p-6 overflow-y-auto space-y-6">
            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
              <h3 className="text-sm font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                <FiGlobe className="w-5 h-5" /> Live Open Source Web Textbooks Scraper
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Search Open Library & Google Books live. Read online or vectorize directly into your RAG database.
              </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={webQuery}
                  onChange={(e) => setWebQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleWebSearch()}
                  placeholder="Enter topic or textbook name (e.g. Operating Systems, Computer Networks)..."
                  className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-500"
                />
                <button
                  onClick={() => handleWebSearch()}
                  disabled={loadingWeb}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs px-6 py-2.5 rounded-xl transition flex items-center gap-2 disabled:opacity-50 cursor-pointer shadow-xs"
                >
                  <FiSearch className="w-4 h-4" /> Scrape Online
                </button>
              </div>
            </div>

            {loadingWeb ? (
              <div className="flex flex-col items-center justify-center h-48 space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Scraping Open Library repository...</span>
              </div>
            ) : webBooks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {webBooks.map((book) => (
                  <div
                    key={book.id}
                    className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 p-4 rounded-2xl transition flex flex-col justify-between space-y-3 shadow-xs"
                  >
                    <div className="space-y-2">
                      <div className="flex gap-3">
                        {book.coverUrl ? (
                          <img src={book.coverUrl} alt={book.title} className="w-12 h-16 object-cover rounded bg-slate-100 dark:bg-slate-900 flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-16 bg-slate-100 dark:bg-slate-900 rounded flex items-center justify-center text-slate-400 flex-shrink-0">
                            <FiGlobe className="w-6 h-6" />
                          </div>
                        )}
                        <div className="overflow-hidden">
                          <span className="text-[9px] uppercase font-bold px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 rounded border border-emerald-200 dark:border-emerald-800">
                            {book.source}
                          </span>
                          <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate mt-1">{book.title}</h4>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">By {book.author}</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-3 leading-relaxed">{book.summary}</p>
                    </div>

                    <div className="pt-3 border-t border-slate-100 dark:border-slate-850 flex items-center justify-between text-xs">
                      <a
                        href={book.readUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 dark:text-cyan-400 hover:underline font-bold flex items-center gap-1 text-xs"
                      >
                        <FiExternalLink className="w-3.5 h-3.5" /> Read Online
                      </a>
                      <button
                        onClick={() => handleVectorizeBook(book)}
                        disabled={vectorizingId === book.id}
                        className="bg-emerald-50 dark:bg-emerald-950 hover:bg-emerald-100 dark:hover:bg-emerald-900 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-bold px-2.5 py-1 rounded-lg text-[10px] transition flex items-center gap-1 disabled:opacity-50 cursor-pointer"
                      >
                        <FiPlusCircle className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> Vectorize
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 text-slate-500 space-y-2 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-200 dark:border-slate-800">
                <FiGlobe className="w-12 h-12 mx-auto text-slate-400 dark:text-slate-700" />
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-400">Search for open-access books online directly.</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AIAssistant;
